import { CheckCheck } from "lucide-react";
import { HEALTH_REMINDERS_ENABLED } from "@shared/constants/features";
import { AppShell } from "../components/AppShell";
import { PlayfulHero } from "../components/PlayfulHero";
import { Button } from "../components/ui/Button";
import { EmptyState } from "../components/ui/EmptyState";
import { Skeleton } from "../components/ui/Skeleton";
import { NotificationList } from "../components/notifications/NotificationList";
import {
  useNotifications,
  useMarkRead,
  useMarkAllRead,
} from "../lib/api/useNotifications";

/**
 * In-app notification center (slice E). Lists the current user's notifications
 * newest-first; a click marks one read (and follows its link when present), and
 * "Mark all read" clears the badge. The bell in the header shares this query,
 * so both stay in sync. Polls every 30s via useNotifications.
 */
export function NotificationCenter() {
  const { data, isLoading, error } = useNotifications();
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  const items = data?.items ?? [];
  const unreadCount = data?.unreadCount ?? 0;
  const hasNotifications = items.length > 0;

  return (
    <AppShell maxWidth="2xl">
      <PlayfulHero
        decor="compact"
        art="send"
        back={{ href: "/dashboard", label: "Back to dashboard" }}
        eyebrow="Notifications"
        title="Notification center"
        description={
          unreadCount > 0
            ? `You have ${unreadCount} unread notification${unreadCount === 1 ? "" : "s"}.`
            : "You're all caught up."
        }
        actions={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => markAllRead.mutate()}
            disabled={unreadCount === 0 || markAllRead.isPending}
            isLoading={markAllRead.isPending}
          >
            <CheckCheck className="h-4 w-4" aria-hidden />
            Mark all read
          </Button>
        }
      />

      <div className="mt-8 sm:mt-10">
        {isLoading && <SkeletonList />}

        {error && (
          <p role="alert" className="rounded-2xl bg-red-50 p-4 text-sm text-red-700">
            {error.message}
          </p>
        )}

        {!isLoading && !error && !hasNotifications && (
          <EmptyState
            art="heart"
            title="No notifications yet"
            description={
              HEALTH_REMINDERS_ENABLED
                ? "When a caregiver opens a share link or a medication nears expiry, it'll show up here."
                : "When a caregiver opens a link you shared, it'll show up here."
            }
          />
        )}

        {!isLoading && !error && hasNotifications && (
          <NotificationList notifications={items} onMarkRead={(id) => markRead.mutate(id)} />
        )}
      </div>
    </AppShell>
  );
}

function SkeletonList() {
  return (
    <ul className="space-y-2" aria-hidden>
      {[0, 1, 2, 3].map((i) => (
        <li key={i} className="flex items-start gap-3 rounded-2xl bg-white/60 px-4 py-3 ring-1 ring-white/80">
          <Skeleton className="mt-1.5 h-2.5 w-2.5 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-56" />
          </div>
        </li>
      ))}
    </ul>
  );
}
